import { pushDL } from '@lendica/api';

// Declare global window.pushDL for A/B testing tracking
declare global {
    interface Window {
        pushDL: (event: string, properties: Record<string, any>) => void;
    }
}

interface LeadData {
    goals: string[];
    email: string;
    firstName?: string;
    lastName?: string;
    company?: string;
    phone?: string;
}

// Tracking interfaces
interface TrackingData {
    treatment?: string;
    variant?: string;
}

interface StepOneTrackingData extends TrackingData {
    goals: string[];
    email: string;
}

interface StepTwoTrackingData extends TrackingData {
    email: string;
    firstName: string;
    lastName: string;
    company: string;
    phone: string;
}

// Function to get HUTK cookie from browser
const getHutkCookie = (): string | null => {
    if (typeof document === 'undefined') return null;

    // Try different possible cookie names
    const cookieNames = ['hubspotutk', '__hstc', 'hubspotutk'];

    for (const cookieName of cookieNames) {
        const match = document.cookie.match(new RegExp(`${cookieName}=([^;]+)`));
        if (match) {
            return match[1];
        }
    }

    return null;
};

// Function to wait for HubSpot script to load and set cookies
const waitForHubSpotCookie = async (maxWaitTime = 3000): Promise<string | null> => {
    const startTime = Date.now();

    while (Date.now() - startTime < maxWaitTime) {
        const hutk = getHutkCookie();
        if (hutk) {
            return hutk;
        }

        // Wait 100ms before trying again
        await new Promise(resolve => setTimeout(resolve, 100));
    }

    return null;
};

// Tracking functions
export const trackLeadFormStep1 = (data: StepOneTrackingData): void => {
    if (typeof window !== 'undefined' && window.pushDL) {
        window.pushDL('ab_test_step', {
            step: 'step_1_completed',
            ab_test_name: 'lead_form',
            ab_test_variant: data.variant || 'control',
            goals: data.goals,
            email: data.email,
        });
    }
};

export const trackLeadFormStep2 = (data: StepTwoTrackingData): void => {
    if (typeof window !== 'undefined' && window.pushDL) {
        window.pushDL('ab_test_step', {
            step: 'step_2_completed',
            ab_test_name: 'lead_form',
            ab_test_variant: data.variant || 'control',
            email: data.email,
            first_name: data.firstName,
            last_name: data.lastName,
            company_name: data.company,
            phone: data.phone,
        });
    }
};

export const trackLeadFormConversion = (email: string, variant?: string): void => {
    if (typeof window !== 'undefined' && window.pushDL) {
        window.pushDL('ab_test_step', {
            step: 'conversion_complete',
            ab_test_name: 'lead_form',
            ab_test_variant: variant || 'control',
            email: email,
            conversion_type: 'otp_sent',
        });
    }
};

export const trackPhoneAuthFromLead = (phone: string, email: string): void => {
    // Use the imported pushDL directly for auth tracking
    pushDL('auth_step', {
        step: 'phone_submit_from_lead',
        phone: phone,
        email: email,
    });
};

export const submitLead = async (leadData: LeadData, throwOnError = true): Promise<void> => {
    try {
        // Wait for HubSpot script to load and set cookies
        const hutk = await waitForHubSpotCookie();

        const response = await fetch('/api/leads', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                ...leadData,
                hutk, // Include HUTK cookie in request
                pageUri: window.location.href,
                pageName: document.title,
            }),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        return result;
    } catch (error) {
        if (throwOnError) {
            throw error;
        } else {
            // Lead submission failed, but continuing with form
        }
    }
};
